import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { listDepartments, deptColor, deptRole, nextMoves, formatDurationShort, quickTasksFor, rankQuickTasks } from '@battleops/shared';
import { useTasks } from '@battleops/client/src/context/TaskContext';
import { useDepartments } from '@battleops/client/src/context/DepartmentContext';
import { useDeptGoals } from '@battleops/client/src/data/useDeptGoals';
import { useTaskModal } from '../hooks';
import { ScoreHero } from './dept/ScoreCard';
import { PinnedProjects } from './dept/PinnedProjects';
import BoardCard from './dept/BoardCard';
import { useHomeProjects } from '@battleops/client/src/data/useDeptGoals';
import { Engine, MoveRow, Sec, TONE } from './dept/DeptParts';
import Icon from './Icon';
import s from './dept/Dept.module.css';

// A department's Home on the web (department mode, a department picked) — the phone's (DeskPanel.js): the score card
// (Lj, Oct 11: "the department card needs to be swapped out with the score card from mobile"), then the department's
// own engine and its projects as cards (pinned to its Home; they take Next moves' place), what needs you and its
// hours this week. Home's generic cards stay on All (v0.1.213: "a department's Home is its own").

/** The department Home is showing, or null (mode off, or All). */
export function useHomeDepartment() {
  const { departmentMode, currentDepartmentId, rawDepartments } = useDepartments();
  return useMemo(() => {
    if (!departmentMode || !currentDepartmentId || currentDepartmentId === 'all') return null;
    return listDepartments(rawDepartments || []).find((d) => d.id === currentDepartmentId) || null;
  }, [departmentMode, currentDepartmentId, rawDepartments]);
}

export const weekOf = () => {
  const today = new Date();
  const monday = new Date(today); monday.setDate(today.getDate() - ((today.getDay() + 6) % 7));
  return `week of ${monday.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })}`;
};

export default function DeskPanel({ dept }) {
  const navigate = useNavigate();
  const details = useTaskModal();
  const { tasks, startQuickTask } = useTasks();
  const [err, setErr] = useState(null);
  const { view, data, week, hours, signals: all } = useDeptGoals(dept);
  const { pinned } = useHomeProjects(dept);
  // the department's quick tasks (one click = clocked onto it) — Field shows its own on site, in its engine
  const quick = useMemo(() => (dept && deptRole(dept) !== 'field' ? rankQuickTasks(quickTasksFor(dept), tasks, Date.now()) : []), [dept, tasks]);
  const moves = useMemo(() => (view ? nextMoves(view, dept, data, { limit: 4 }) : []), [view, dept, data]);
  const showMoves = !pinned;   // the apps take Next moves' place
  const signals = useMemo(() => (showMoves ? all.filter((x) => !x.taskId || !moves.some((m) => m.task.id === x.taskId)) : all), [all, moves, showMoves]);   // not twice
  if (!dept || !view) return null;
  const worked = hours?.workedMs || 0;
  const planned = hours?.plannedMs || 0;
  const open = () => navigate(`/department/${dept.id}`);
  const needRows = signals.slice(0, 5).map((x, i) => (
    <button key={`${x.kind}${i}`} type="button" className={`${s.row} ${s.rowBtn}`} onClick={() => (x.taskId ? details.open(x.taskId) : x.projectId ? navigate(`/projects/${x.projectId}`) : null)}>
      <Icon name={x.kind === 'overdue' ? 'clock' : x.kind === 'over' ? 'alert-triangle' : x.kind === 'receipt' ? 'file-text' : 'briefcase'} size={15} />
      <span className={s.rowText}>{x.title}</span>{x.detail ? <span className={`${s.faint} ${s.num}`}>{x.detail}</span> : null}
    </button>
  ));
  const hoursRight = <span className={s.num} style={{ color: 'var(--c-text)', fontSize: 13 }}>{formatDurationShort(worked)}{planned ? ` of ${formatDurationShort(planned)} planned` : ''}</span>;
  const hoursBar = (
    <>
      <div className={s.track} style={{ height: 8 }}><div className={s.fill} style={{ width: `${Math.min(100, planned ? (worked / planned) * 100 : worked ? 100 : 0)}%`, background: planned && worked > planned * 1.15 ? TONE.close : undefined }} /></div>
      {!planned ? <div className={s.faint} style={{ marginTop: 8 }}>Nothing planned here this week yet.</div> : null}
    </>
  );
  return (
    <div style={{ '--a': deptColor(dept) }}>
      <ScoreHero dept={dept} view={view} week={week} data={data} subtitle={weekOf()} onOpen={(goalId) => navigate(`/department/${dept.id}${goalId ? `?goal=${goalId}` : view.goals.length ? '' : '?add=1'}`)} />
      {quick.length ? (
        <div className={s.quick} style={{ marginBottom: 4 }}>
          {quick.map((q) => <button key={q.id} type="button" className={s.qt} style={{ flex: '0 1 auto', padding: '0 14px' }} onClick={() => { setErr(null); try { startQuickTask(dept.id, q.id); } catch (e) { setErr(String(e?.message || e)); } }} title={q.scope === 'home' ? 'On the department’s home project' : 'On the job you’re on'}><Icon name="play" size={13} />{q.title}</button>)}
        </div>
      ) : null}
      {err ? <div style={{ color: 'var(--c-danger)', fontSize: 13, marginBottom: 12 }}>{err}</div> : null}
      <div className={s.grid}>
          <div className={s.colMain}>
            <Engine dept={dept} view={view} data={data} />
            {/* every department's projects as cards (v0.1.256: "personal projects should be cards like the app section") */}
            <PinnedProjects dept={dept} compact />
            <BoardCard dept={dept} data={data} />{/* the department's board as mini bars, when its board is on (Marketing…) */}
          </div>
          <div className={s.colSide}>
            {/* a department with projects pinned shows their cards instead of Next moves (Lj, Oct 11); none = Next moves */}
            {!pinned && moves.length ? (
              <Sec title="Next moves">
                {moves.map((m, i) => <MoveRow key={m.task.id} icon={m.goalId ? 'target' : 'play'} title={m.task.title} why={m.why} whyTone={m.why.startsWith('overdue') ? TONE.over : null} primary={i === 0} onGo={() => details.open(m.task.id)} onOpen={() => details.open(m.task.id)} />)}
              </Sec>
            ) : null}
            {signals.length ? <Sec title="Needs you">{needRows}</Sec> : null}
            <Sec title={`${dept.name} hours this week`} right={hoursRight}>{hoursBar}</Sec>
            <button type="button" className={s.add} onClick={open}><Icon name="bar-chart-2" size={15} />THE BREAKDOWN · EDIT GOALS</button>
          </div>
        </div>
    </div>
  );
}
